/**
 * TabBar — pasek kart powłoki (wzorzec SellCall `components/TabBar.tsx`).
 *
 * - Karta: ikona + etykieta + X (zamknięcie, stale widoczny). Aktywna podświetlona.
 * - Środkowy przycisk myszy zamyka kartę; prawy klik → menu (Zamknij / Zamknij pozostałe /
 *   Zamknij wszystkie).
 * - Widoczny przycisk „Zamknij wszystkie" na końcu paska (poza obszarem przewijania).
 * - Karta przypięta (Pulpit, `basePath === defaultPath`) bez X — nieusuwalna.
 * - Przy dużej liczbie kart: poziome przewijanie + STRZAŁKI ‹ › na końcach (gdy overflow)
 *   + menu „▾ wszystkie karty" (skok do dowolnej, także poza ekranem). Aktywna karta sama
 *   wjeżdża w widok.
 *
 * Scope-agnostyczny: czyta wszystko z `useTabs()`. Używany w powłoce tenanta i admina.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

import { lookupMenuMeta, useTenantMenuIconMap, useTenantMenuLabelMap } from '@/components/Sidebar';
import { iconColorByName } from '@/components/sidebar-icon';
import { Tooltip } from '@/components/Tooltip';
import { SIDEBAR_ICONS_COLORFUL } from '@/config/ui';
import type { Tab } from '@/contexts/TabContext';
import { useTabs } from '@/contexts/TabContext';
import { useHostScope } from '@/hooks/useHostScope';
import { useSidebarSkin } from '@/hooks/useSidebarSkin';
import { phosphorIconFor } from '@/lib/phosphor-icons';

const SCROLL_STEP = 220;

export function TabBar() {
  const { tabs, activeTabId, defaultPath, switchTab, closeTab, closeOtherTabs, closeAllTabs } =
    useTabs();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const skin = useSidebarSkin();
  // Ikona zakładki = ikona pozycji menu tenanta dla tej trasy (spójność z lewym menu;
  // ustawiana w konfiguracji menu, nie na sztywno). Fallback na dawną mapę `tab.icon`
  // dla tras spoza menu. Poza scope'em tenanta mapa pusta → zawsze fallback.
  const scope = useHostScope();
  const menuIcons = useTenantMenuIconMap(scope === 'tenant');
  const menuIconNameFor = useCallback(
    // Klucz tożsamości (ścieżka + zawężenie do typów / `?mi=`) — dwie pozycje menu na tę samą
    // listę z innym zawężeniem mają osobne ikony/etykiety; fallback na samą ścieżkę (tam
    // leży tylko pozycja bez zawężenia i bez `?mi=`), dalej na `tab.icon` / `tab.label`.
    (tab: Tab): string | undefined => lookupMenuMeta(menuIcons, tab.path),
    [menuIcons],
  );
  // Etykieta zakładki = nazwa pozycji menu tenanta dla tej trasy (spójność z lewym menu;
  // /modules/<slug> dostaje nazwę kartoteki/modułu zamiast generycznego „Moduły"). Fallback
  // na `tab.label` ze słownika kart dla tras spoza menu i szczegółów rekordów.
  const menuLabels = useTenantMenuLabelMap(scope === 'tenant');
  const tabLabelFor = useCallback(
    (tab: Tab): string => lookupMenuMeta(menuLabels, tab.path) ?? tab.label,
    [menuLabels],
  );
  const renderTabIcon = useCallback(
    (tab: Tab, sizeClass: string) => {
      // Zakładki w stylu DUOTONE (Phosphor) — pilot 2026-09-16. Nazwa ikony (menu > fallback
      // `tab.icon`) mapowana na komponent Phosphor; brak mapowania → neutralny `Cube`.
      const Icon = phosphorIconFor(menuIconNameFor(tab) ?? tab.icon);
      return <Icon weight="duotone" className={sizeClass} aria-hidden="true" />;
    },
    [menuIconNameFor],
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; tabId: string } | null>(
    null,
  );
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const [allTabsOpen, setAllTabsOpen] = useState(false);

  const isPinned = (basePath: string) => basePath === defaultPath;

  // Czy pasek przewija się w lewo/prawo (do pokazania strzałek + menu).
  const updateOverflow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  // Nasłuch scroll + resize kontenera, plus przeliczenie gdy zmienia się liczba kart.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateOverflow();
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      ro.disconnect();
    };
  }, [updateOverflow]);

  useEffect(() => {
    updateOverflow();
  }, [tabs, updateOverflow]);

  // Zamykanie menu kontekstowego klikiem gdziekolwiek.
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener('click', handler);
    window.addEventListener('contextmenu', handler);
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('contextmenu', handler);
    };
  }, [contextMenu]);

  // Zamykanie menu „wszystkie karty" klikiem poza nim (toggle robi stopPropagation).
  useEffect(() => {
    if (!allTabsOpen) return;
    const handler = () => setAllTabsOpen(false);
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, [allTabsOpen]);

  // Przewiń aktywną kartę do widoku.
  useEffect(() => {
    if (!activeTabId || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(
      `[data-tab-id="${activeTabId}"]`,
    ) as HTMLElement | null;
    // `scrollIntoView` nie istnieje w jsdom (testy) — wołamy opcjonalnie.
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeTabId]);

  const scrollBy = (dir: -1 | 1) => {
    scrollRef.current?.scrollBy({ left: dir * SCROLL_STEP, behavior: 'smooth' });
  };

  const hasClosable = tabs.some((t) => !isPinned(t.basePath));
  // Czy jest co zamknąć przy „Zamknij inne" — istnieje karta inna niż aktywna i nieprzypięta.
  const hasClosableOthers = tabs.some((t) => t.id !== activeTabId && !isPinned(t.basePath));
  const hasOverflow = overflow.left || overflow.right;

  const arrowClass =
    'flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-bg-tertiary hover:text-text-primary disabled:cursor-default disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-text-tertiary';

  return (
    <div className="flex items-center gap-1.5" data-testid="tab-bar" data-skin={skin}>
      {/* Strzałka w lewo — gdy jest co przewinąć. */}
      {hasOverflow && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          disabled={!overflow.left}
          aria-label={t('tabs.scrollLeft')}
          title={t('tabs.scrollLeft')}
          className={arrowClass}
          data-testid="tab-bar-scroll-left"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex flex-1 items-center gap-1.5 overflow-x-auto py-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const pinned = isPinned(tab.basePath);
          return (
            <div
              key={tab.id}
              data-tab-id={tab.id}
              data-active={isActive ? 'true' : 'false'}
              data-testid={`tab-${tab.basePath}`}
              onClick={() => switchTab(tab.id)}
              onMouseDown={(e) => {
                if (e.button === 1 && !pinned) {
                  e.preventDefault();
                  closeTab(tab.id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, tabId: tab.id });
              }}
              className={`group flex h-10 max-w-[220px] shrink-0 cursor-pointer select-none items-center gap-2 rounded-full border pl-3.5 text-[13px] transition-colors ${
                pinned ? 'pr-4' : 'pr-2'
              } ${
                isActive
                  ? 'border-[#4064c9] bg-bg-elevated font-medium text-[#4064c9] shadow-[0_1px_2px_rgba(22,24,31,0.04),0_2px_6px_-1px_rgba(22,24,31,0.07)] dark:border-accent-500 dark:bg-accent-500/15 dark:text-accent-200'
                  : 'border-[#d2d2d2] bg-white/85 text-text-secondary hover:bg-white dark:border-white/15 dark:bg-white/5 dark:hover:bg-white/10'
              }`}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center"
                // Skórka „Ant" (wg Figmy): ikony zakładek MONOCHROMATYCZNE — dziedziczą kolor
                // tekstu zakładki (szary nieaktywna / niebieski aktywna), bez kolorów per-nazwa.
                style={
                  SIDEBAR_ICONS_COLORFUL && skin !== 'antd'
                    ? { color: iconColorByName(menuIconNameFor(tab) ?? tab.icon) }
                    : undefined
                }
                aria-hidden="true"
              >
                {renderTabIcon(tab, 'h-5 w-5')}
              </span>
              <Tooltip content={tabLabelFor(tab)}>
                <span className="flex-1 truncate">{tabLabelFor(tab)}</span>
              </Tooltip>
              {tabs.length > 1 && !pinned && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    closeTab(tab.id);
                  }}
                  aria-label={t('tabs.closeTab')}
                  title={t('tabs.closeTab')}
                  data-testid={`tab-${tab.basePath}-close`}
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded opacity-60 transition-all hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/10"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}

        {/* „+" (nowa karta) — kwadratowy kafelek w stylu card-tab, wg Figmy „Tabs add button".
            Tenant: otwiera „Mapę obszarów" jako launcher. `div role=button` — spójnie z samymi
            zakładkami (też klikalne divy), bez surowego <button>. */}
        {scope === 'tenant' && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => navigate('/area-map')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate('/area-map');
              }
            }}
            aria-label={t('tenant.sidebar.accessMap.title')}
            title={t('tenant.sidebar.accessMap.title')}
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/60 text-xl leading-none text-text-secondary transition-colors hover:bg-white hover:text-text-primary dark:bg-white/5 dark:hover:bg-white/10"
            data-testid="tab-bar-add"
          >
            +
          </div>
        )}
      </div>

      {/* Strzałka w prawo — gdy jest co przewinąć. */}
      {hasOverflow && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          disabled={!overflow.right}
          aria-label={t('tabs.scrollRight')}
          title={t('tabs.scrollRight')}
          className={arrowClass}
          data-testid="tab-bar-scroll-right"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}

      {/* Menu „wszystkie karty" — skok do dowolnej (także poza ekranem). Tylko gdy overflow. */}
      {hasOverflow && (
        <div className="relative shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAllTabsOpen((v) => !v);
            }}
            aria-label={t('tabs.allTabs')}
            title={t('tabs.allTabs')}
            className={arrowClass}
            data-testid="tab-bar-all-tabs"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
          {allTabsOpen && (
            <div
              className="absolute right-0 top-full z-[100] mt-1 max-h-[60vh] min-w-[220px] overflow-auto rounded-lg border border-border-primary bg-bg-primary py-1 shadow-xl"
              data-testid="tab-bar-all-tabs-menu"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    switchTab(tab.id);
                    setAllTabsOpen(false);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${
                    tab.id === activeTabId
                      ? 'bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-300'
                      : 'text-text-secondary hover:bg-bg-tertiary'
                  }`}
                  data-testid={`tab-bar-all-tabs-item-${tab.basePath}`}
                >
                  <span
                    className="flex h-4 w-4 shrink-0 items-center justify-center"
                    style={
                      SIDEBAR_ICONS_COLORFUL && skin !== 'antd'
                        ? { color: iconColorByName(menuIconNameFor(tab) ?? tab.icon) }
                        : undefined
                    }
                    aria-hidden="true"
                  >
                    {renderTabIcon(tab, 'h-4 w-4')}
                  </span>
                  <span className="truncate">{tabLabelFor(tab)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Zamknij inne — zostawia aktywną (i przypiętą) kartę. */}
      {hasClosableOthers && activeTabId && (
        <button
          type="button"
          onClick={() => closeOtherTabs(activeTabId)}
          className="flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-[13px] text-text-tertiary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          data-testid="tab-bar-close-others"
          title={t('tabs.closeOthers')}
        >
          <span className="hidden sm:inline">{t('tabs.closeOthers')}</span>
          <span className="sm:hidden">
            <X className="h-3.5 w-3.5" />
          </span>
        </button>
      )}

      {/* Zamknij wszystkie — widoczny przycisk poza obszarem przewijania. */}
      {hasClosable && (
        <button
          type="button"
          onClick={() => closeAllTabs()}
          className="flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-[13px] text-text-tertiary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          data-testid="tab-bar-close-all"
          title={t('tabs.closeAll')}
        >
          <X className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t('tabs.closeAll')}</span>
        </button>
      )}

      {/* Menu kontekstowe (prawy klik na karcie). */}
      {contextMenu && (
        <div
          className="fixed z-[100] min-w-[180px] rounded-lg border border-border-primary bg-bg-primary py-1 shadow-xl"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          data-testid="tab-context-menu"
        >
          {!isPinned(tabs.find((t) => t.id === contextMenu.tabId)?.basePath ?? '') && (
            <button
              type="button"
              onClick={() => {
                closeTab(contextMenu.tabId);
                setContextMenu(null);
              }}
              className="w-full px-3 py-1.5 text-left text-sm text-text-secondary hover:bg-bg-tertiary"
            >
              {t('tabs.closeTab')}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              closeOtherTabs(contextMenu.tabId);
              setContextMenu(null);
            }}
            className="w-full px-3 py-1.5 text-left text-sm text-text-secondary hover:bg-bg-tertiary"
          >
            {t('tabs.closeOthers')}
          </button>
          <button
            type="button"
            onClick={() => {
              closeAllTabs();
              setContextMenu(null);
            }}
            className="w-full px-3 py-1.5 text-left text-sm text-text-secondary hover:bg-bg-tertiary"
          >
            {t('tabs.closeAll')}
          </button>
        </div>
      )}
    </div>
  );
}
